import { GetStaticProps } from "next";
import { css } from "@emotion/react";

import Layout from "@/components/Layout/Layout";
import Container from "@/components/Container/Container";

import ScheduleGeneratorFeatureSection from "@/components/ScheduleGenerator/ScheduleGeneratorFeatureSection";
import ScheduleGeneratorHeroSection from "@/components/ScheduleGenerator/ScheduleGeneratorHeroSection";

import PageFAQSection from "@/components/PageFAQSection";

import { contentfulClient } from "@/utils/contentful";
import { mq } from "@/constants/breakpoints";
import { useMemo } from "react";
import { Heading } from "@/components/Typography";

import FeatureSectionContentful from "@/components/FeatureSection/FeatureSectionContentful";

const Page = ({ fields }: any) => {
  const faqItems = useMemo(
    () =>
      fields?.faqItems?.map((item: any) => ({
        question: item.fields.question,
        answer: item.fields.answer,
      })) || [],
    [fields]
  );
  return (
    <>
      <Layout
        title={fields.pageTitle}
        metaDescription={fields.metaDescription}
        heroContainerCss={css`
          background-image: url("/images/homepage/BG.svg"),
            linear-gradient(112.24deg, #048cdd 4.51%, #32b2ff 100.81%);
          background-size: cover;
          background-repeat: no-repeat;
          position: relative;
          z-index: 1;
        `}
        hero={<ScheduleGeneratorHeroSection fields={fields} />}
      >
        <Container
          css={css`
            text-align: center;
            background-color: white;
          `}
        >
          <Heading
            level={2}
            css={css`
              font-size: 10px;
              font-weight: 350;
              line-height: 12px;
              text-align: center;
              letter-spacing: 0%;
              text-transform: uppercase;
              color: #048cdd;
              margin-top: 60px;
              ${mq["lg"]} {
                font-size: 24px;
                line-height: 31.21px;
                margin-top: 120px;
              }
            `}
          >
            SPORTS SCHEDULE GENERATOR
          </Heading>
          <ScheduleGeneratorFeatureSection fields={fields} />
        </Container>

        {(fields.pageSections || []).map((section: any, index: number) => {
          return (
            <FeatureSectionContentful
              key={section.sys.id || `section-${index}`}
              imageSrc={
                section.fields.image.fields.file.url.startsWith("//")
                  ? `https:${section.fields.image.fields.file.url}`
                  : section.fields.image.fields.file.url
              }
              sectionHeadline={section.fields.sectionTitle}
              feat1Headline={section.fields.item1Title}
              feat1Body={section.fields.item1Description}
              feat2Headline={section.fields.item2Title}
              feat2Body={section.fields.item2Description}
              feat3Headline={section.fields.item3Title}
              feat3Body={section.fields.item3Description}
              feat4Headline={section.fields.item4Title}
              feat4Body={section.fields.item4Description}
            />
          );
        })}
        <div
          css={css`
            margin-top: 60px;
            ${mq["lg"]} {
              margin-top: 120px;
            }
          `}
        >
          <PageFAQSection faqItems={faqItems} />
        </div>
      </Layout>
    </>
  );
};

export const getStaticProps: GetStaticProps = async () => {
  try {
    const entries = await contentfulClient.getEntries({
      content_type: "scheduleGeneratorPage",
    });

    // Check if entries and items exist before accessing fields
    const fields = entries?.items?.[0]?.fields || {};

    return {
      props: { fields },
    };
  } catch (error) {
    console.error("Error fetching schedule generator data:", error);
    // Return empty fields if there's an error
    return {
      props: { fields: {} },
    };
  }
};

export default Page;
